/**
 * Chassis-page Administration tab — internal horizontal sub-tabs
 * over the low-frequency surfaces operators only touch occasionally:
 *
 *   Chassis info  │  Overrides  │  Auto schedules
 *
 * Replaces the v2.0-initial collapsibles bottom-of-page region. URL
 * hash carries the sub-tab as ``#tab=admin&sub=overrides`` so a
 * deep-link can land directly on the overrides editor without an
 * extra click.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import { CopyableCell } from "../../../components/CopyableCell";
import { Tabs, type TabSpec } from "../../../components/Tabs";
import { useToast } from "../../../components/Toast";
import { AutoProvisionPanel } from "./AutoProvisionPanel";

// Adapters with a per-site auto-provision template + watcher arm.
// Mirrors the backend "auto_provision" adapter capability — Nokia is a
// one-line add here once its template shape + worker arm land.
const ADAPTERS_WITH_AUTO_PROVISION = new Set(["radisys:gpon"]);

type ChassisOut = {
  id: number;
  gigafy_device_id: string | null;
  gigafy_device_name: string | null;
  gigafy_location_id: string | null;
  gigafy_location_name: string | null;
  // v0.14.1 — operator-set friendly name. Resolves over
  // gigafy_device_name in every UI surface via effective_device_name.
  device_name_override: string | null;
  effective_device_name: string;
  adapter_key: string;
  host_synced: string | null;
  host_override: string | null;
  ssh_port_override: number | null;
  ssh_username_override: string | null;
  password_set: boolean;
  concurrent_session_limit: number;
  enabled: boolean;
  last_seen_at: string | null;
  effective_host: string | null;
  effective_port: number;
  effective_username: string | null;
};

type Schedule = {
  id: number;
  name: string;
  job_name: string;
  cron_expr: string | null;
  interval_seconds: number | null;
  policy: string;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
};

type SchedulesResponse = { schedules: Schedule[] };

function readSubTab(fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const hash = window.location.hash || "";
  const match = hash.match(/[#&]sub=([\w-]+)/);
  return match ? match[1] : fallback;
}

function writeSubTab(value: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const hash = url.hash || "#";
  // Strip existing ``sub=...`` if present, then append.
  const cleaned = hash.replace(/[&#]sub=[\w-]+/, "");
  url.hash = (cleaned || "#") + (cleaned.includes("=") ? "&" : "") + `sub=${value}`;
  history.replaceState(null, "", url.toString());
}

export function AdministrationTab({
  chassisId,
  chassis,
}: {
  chassisId: string;
  chassis: ChassisOut;
}) {
  const [sub, setSub] = useState<string>(() => readSubTab("info"));

  const onSubChange = useCallback((next: string) => {
    setSub(next);
    writeSubTab(next);
  }, []);

  // Listen for hash changes (back/forward, deep-link).
  useEffect(() => {
    const onHash = () => setSub(readSubTab("info"));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const subTabs = useMemo<TabSpec[]>(
    () => [
      {
        value: "info",
        label: "Chassis info",
        content: <ChassisInfoPanel chassis={chassis} />,
      },
      {
        value: "overrides",
        label: "Overrides",
        content: <OverridesPanel chassis={chassis} />,
      },
      {
        value: "schedules",
        label: "Auto schedules",
        content: <SchedulesPanel chassisId={chassisId} />,
      },
      ...(ADAPTERS_WITH_AUTO_PROVISION.has(chassis.adapter_key)
        ? [
            {
              value: "auto-provision",
              label: "Auto provision",
              content: (
                <AutoProvisionPanel
                  chassisId={chassisId}
                  adapterKey={chassis.adapter_key}
                  locationId={chassis.gigafy_location_id}
                  locationName={chassis.gigafy_location_name}
                />
              ),
            },
          ]
        : []),
    ],
    [chassis, chassisId],
  );

  return <Tabs tabs={subTabs} value={sub} onValueChange={onSubChange} />;
}

function ChassisInfoPanel({ chassis }: { chassis: ChassisOut }) {
  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <dt className="text-slate-500">Adapter</dt>
        <dd>
          <Badge tone="info">{chassis.adapter_key}</Badge>
        </dd>
        <dt className="text-slate-500">State</dt>
        <dd>
          <Badge tone={chassis.enabled ? "success" : "muted"}>
            {chassis.enabled ? "enabled" : "disabled"}
          </Badge>
        </dd>
        <dt className="text-slate-500">Device ID</dt>
        <dd>
          <CopyableCell value={chassis.gigafy_device_id} />
        </dd>
        <dt className="text-slate-500">Synced host</dt>
        <dd>
          <CopyableCell value={chassis.host_synced} />
        </dd>
        <dt className="text-slate-500">Effective host:port</dt>
        <dd>
          <CopyableCell
            value={`${chassis.effective_host ?? "—"}:${chassis.effective_port}`}
          />
        </dd>
        <dt className="text-slate-500">Effective username</dt>
        <dd>
          <CopyableCell value={chassis.effective_username} />
        </dd>
        <dt className="text-slate-500">Password set</dt>
        <dd className="font-mono">{chassis.password_set ? "yes" : "no"}</dd>
        <dt className="text-slate-500">Session limit</dt>
        <dd className="font-mono">{chassis.concurrent_session_limit}</dd>
        <dt className="text-slate-500">Last seen</dt>
        <dd className="font-mono text-[10px] text-slate-500">
          <LocalTime iso={chassis.last_seen_at} />
        </dd>
      </dl>
    </section>
  );
}

function OverridesPanel({ chassis }: { chassis: ChassisOut }) {
  // Inline editor — PUT /api/chassis/{id}/overrides accepts a sparse
  // body (PATCH semantics): only present fields are touched. Sending
  // a key with empty-string clears the override and falls back to
  // the Gigafy-synced value (handled below by mapping "" → null).
  const qc = useQueryClient();
  const toast = useToast();

  const [deviceNameOverride, setDeviceNameOverride] = useState(
    chassis.device_name_override ?? "",
  );
  const [hostOverride, setHostOverride] = useState(chassis.host_override ?? "");
  const [portOverride, setPortOverride] = useState(
    chassis.ssh_port_override != null ? String(chassis.ssh_port_override) : "",
  );
  const [usernameOverride, setUsernameOverride] = useState(
    chassis.ssh_username_override ?? "",
  );
  const [passwordOverride, setPasswordOverride] = useState("");
  const [sessionLimit, setSessionLimit] = useState(
    String(chassis.concurrent_session_limit),
  );
  const [enabled, setEnabled] = useState(chassis.enabled);

  // Re-seed form state if the upstream chassis row reloads (e.g.
  // after another operator edits it). Without this the form would
  // get stuck at the snapshot the page first rendered with.
  useEffect(() => {
    setDeviceNameOverride(chassis.device_name_override ?? "");
    setHostOverride(chassis.host_override ?? "");
    setPortOverride(
      chassis.ssh_port_override != null ? String(chassis.ssh_port_override) : "",
    );
    setUsernameOverride(chassis.ssh_username_override ?? "");
    setSessionLimit(String(chassis.concurrent_session_limit));
    setEnabled(chassis.enabled);
    // password_override deliberately not seeded — it's always blank
    // on the form so the operator must re-enter to update it.
  }, [chassis]);

  const save = useMutation({
    mutationFn: async () => {
      // PATCH semantics — only send keys that changed OR that
      // operator explicitly cleared (empty string → null).
      const body: Record<string, unknown> = {};
      const trimOrNull = (s: string) => (s.trim() === "" ? null : s.trim());

      if (trimOrNull(deviceNameOverride) !== (chassis.device_name_override ?? null)) {
        body.device_name_override = trimOrNull(deviceNameOverride);
      }
      if (trimOrNull(hostOverride) !== (chassis.host_override ?? null)) {
        body.host_override = trimOrNull(hostOverride);
      }
      const portValue = portOverride.trim() === "" ? null : parseInt(portOverride, 10);
      if (portValue !== chassis.ssh_port_override) {
        body.ssh_port_override = portValue;
      }
      if (trimOrNull(usernameOverride) !== (chassis.ssh_username_override ?? null)) {
        body.ssh_username_override = trimOrNull(usernameOverride);
      }
      if (passwordOverride.trim() !== "") {
        // Only send password when the operator typed a new value;
        // empty stays-as-is (don't accidentally wipe a stored
        // password just because the field rendered blank).
        body.ssh_password_override = passwordOverride;
      }
      const limitValue = parseInt(sessionLimit, 10);
      if (Number.isFinite(limitValue) && limitValue !== chassis.concurrent_session_limit) {
        body.concurrent_session_limit = limitValue;
      }
      if (enabled !== chassis.enabled) {
        body.enabled = enabled;
      }
      if (Object.keys(body).length === 0) {
        // Nothing changed — short-circuit so the operator gets
        // immediate feedback rather than an empty PUT.
        return null;
      }
      const res = await api.put(`/chassis/${chassis.id}/overrides`, body);
      return res.data;
    },
    onSuccess: (data) => {
      if (data === null) {
        toast.push("No changes to save", "info");
        return;
      }
      toast.push("Overrides saved", "success");
      // Re-fetch the chassis row so the read-only Chassis info panel
      // + the form's effective values reflect the new state.
      qc.invalidateQueries({ queryKey: ["v2", "chassis", String(chassis.id)] });
      setPasswordOverride("");
    },
    onError: () => toast.push("Save failed — check console", "error"),
  });

  const dirty = (() => {
    const trimOrNull = (s: string) => (s.trim() === "" ? null : s.trim());
    const portValue = portOverride.trim() === "" ? null : parseInt(portOverride, 10);
    return (
      trimOrNull(deviceNameOverride) !== (chassis.device_name_override ?? null) ||
      trimOrNull(hostOverride) !== (chassis.host_override ?? null) ||
      portValue !== chassis.ssh_port_override ||
      trimOrNull(usernameOverride) !== (chassis.ssh_username_override ?? null) ||
      passwordOverride.trim() !== "" ||
      parseInt(sessionLimit, 10) !== chassis.concurrent_session_limit ||
      enabled !== chassis.enabled
    );
  })();

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-slate-200">Overrides</h3>
        <span className="text-[10px] text-slate-500">
          Leave blank → fall back to the Gigafy-synced value.
        </span>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="grid gap-3 sm:grid-cols-2"
      >
        <Field
          label="Device name"
          hint={
            chassis.gigafy_device_name
              ? `Gigafy: ${chassis.gigafy_device_name}`
              : "(no name from Gigafy — set a friendly name here)"
          }
        >
          <input
            type="text"
            value={deviceNameOverride}
            onChange={(e) => setDeviceNameOverride(e.target.value)}
            placeholder={chassis.gigafy_device_name ?? "Friendly name"}
            className={inputClass}
            maxLength={200}
          />
        </Field>
        <Field
          label="Host override"
          hint={chassis.host_synced ? `synced: ${chassis.host_synced}` : "no synced host"}
        >
          <input
            type="text"
            value={hostOverride}
            onChange={(e) => setHostOverride(e.target.value)}
            placeholder={chassis.host_synced ?? "10.0.0.1"}
            className={inputClass}
            spellCheck={false}
          />
        </Field>
        <Field label="SSH port override" hint="default: 22">
          <input
            type="number"
            min={1}
            max={65535}
            value={portOverride}
            onChange={(e) => setPortOverride(e.target.value)}
            placeholder="22"
            className={inputClass}
          />
        </Field>
        <Field
          label="SSH username override"
          hint={
            chassis.effective_username
              ? `effective: ${chassis.effective_username}`
              : "uses Gigafy creds when blank"
          }
        >
          <input
            type="text"
            value={usernameOverride}
            onChange={(e) => setUsernameOverride(e.target.value)}
            placeholder="(leave blank for Gigafy default)"
            className={inputClass}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field
          label="SSH password override"
          hint={chassis.password_set ? "currently SET (leave blank to keep)" : "currently EMPTY"}
        >
          <input
            type="password"
            value={passwordOverride}
            onChange={(e) => setPasswordOverride(e.target.value)}
            placeholder={chassis.password_set ? "•••••••• (keeping current)" : "(blank — use Gigafy)"}
            className={inputClass}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Concurrent session limit" hint="1–32; default: 1">
          <input
            type="number"
            min={1}
            max={32}
            value={sessionLimit}
            onChange={(e) => setSessionLimit(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Enabled" hint="disabled chassis refuse all jobs">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span>{enabled ? "enabled" : "disabled"}</span>
          </label>
        </Field>

        <div className="sm:col-span-2 flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={!dirty || save.isPending}
            className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-3 w-3" />
            {save.isPending ? "Saving…" : "Save overrides"}
          </button>
          {dirty && (
            <button
              type="button"
              onClick={() => {
                setDeviceNameOverride(chassis.device_name_override ?? "");
                setHostOverride(chassis.host_override ?? "");
                setPortOverride(
                  chassis.ssh_port_override != null
                    ? String(chassis.ssh_port_override)
                    : "",
                );
                setUsernameOverride(chassis.ssh_username_override ?? "");
                setPasswordOverride("");
                setSessionLimit(String(chassis.concurrent_session_limit));
                setEnabled(chassis.enabled);
              }}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              <X className="h-3 w-3" />
              Discard
            </button>
          )}
          {save.isSuccess && save.data !== null && (
            <Badge tone="success">saved</Badge>
          )}
        </div>
      </form>
    </section>
  );
}

const inputClass =
  "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-slate-400">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
    </label>
  );
}

function SchedulesPanel({ chassisId }: { chassisId: string }) {
  const q = useQuery<SchedulesResponse>({
    queryKey: ["v2", "chassis", chassisId, "schedules"],
    queryFn: async () => {
      const res = await api.get<SchedulesResponse>(
        `/chassis/${chassisId}/schedules`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });
  const schedules = q.data?.schedules ?? [];
  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-slate-200">Auto schedules</h3>
        <Link
          to="/settings/schedules"
          className="text-xs text-emerald-400 hover:text-emerald-300"
        >
          Manage in Settings →
        </Link>
      </div>
      {q.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : schedules.length === 0 ? (
        <p className="text-xs text-slate-500">
          No schedules attached to this chassis. Create one in
          Settings → Schedules.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1">Name</th>
              <th className="px-2 py-1">Job</th>
              <th className="px-2 py-1">Trigger</th>
              <th className="px-2 py-1">Policy</th>
              <th className="px-2 py-1">State</th>
              <th className="px-2 py-1">Next run</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/40 text-slate-300">
            {schedules.map((s) => (
              <tr key={s.id}>
                <td className="px-2 py-1.5">
                  <Link
                    to={`/settings/schedules/${s.id}`}
                    className="text-emerald-400 hover:text-emerald-300"
                  >
                    {s.name}
                  </Link>
                </td>
                <td className="px-2 py-1.5 font-mono">{s.job_name}</td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">
                  {s.cron_expr ?? `every ${s.interval_seconds}s`}
                </td>
                <td className="px-2 py-1.5 text-[11px]">{s.policy}</td>
                <td className="px-2 py-1.5">
                  <Badge tone={s.enabled ? "success" : "muted"}>
                    {s.enabled ? "enabled" : "disabled"}
                  </Badge>
                </td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                  <LocalTime iso={s.next_run_at} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
